import React, { useState } from 'react';
import IconMC from './IconMC';
import useClickOutside from '../../hooks/useClickOutside';

/**
 * FilterDropdownMC - Molecule Component
 * 
 * Dropdown component specifically designed for filters with checkbox selection
 * Features red dot indicator when filters are active and auto-adjusting height
 * Based on DropdownMC styles with checkbox visual indicators
 * Handles click outside functionality and dropdown state internally
 * Automatically adjusts sizing based on container constraints
 * 
 * @param {Array} options - Array of filter options [{id, label}]
 * @param {Array} selectedValues - Array of currently selected option IDs
 * @param {Function} onItemToggle - Callback when an option is toggled
 * @param {string} placeholder - Placeholder text to display
 * @param {string} [className] - Additional CSS classes for container
 * @param {Object} [styles] - Custom style overrides
 * @param {boolean} [showRedDot=true] - Show red dot indicator when items selected
 * @param {string} [redDotPosition='top-1.5 right-1.5'] - Position classes for red dot
 * @param {boolean} [showCheckboxes=true] - Show checkbox indicators in dropdown items
 * @param {string} [checkboxStyle='blue'] - Color theme for checkboxes (blue, green, red)
 * @param {Function} [customDisplayText] - Custom function to format display text
 * @param {string} [iconName] - Custom arrow icon name
 * @param {string} [iconSize='base'] - Size of the arrow icon
 */
const FilterDropdownMC = ({ 
  options = [], 
  selectedValues = [], 
  onItemToggle, 
  placeholder = '',
  className = '',
  styles = {},
  showRedDot = true,
  redDotPosition = 'tw-top-1.5 tw-right-1.5',
  showCheckboxes = true,
  checkboxStyle = 'blue',
  customDisplayText,
  iconName = 'arrow-ios',
  iconSize = 'base'
}) => {
  
  const [isOpen, setIsOpen] = useState(false);
  
  const dropdownRef = useClickOutside(() => {
    setIsOpen(false);
  });

  const getResponsivePadding = () => {
    return 'tw-px-3 tw-py-1';
  };

  const checkboxThemes = {
    blue: {
      selected: 'tw-bg-blue-500',
      icon: 'tw-text-white'
    },
    green: {
      selected: 'tw-bg-green-500',
      icon: 'tw-text-white'
    },
    red: {
      selected: 'tw-bg-red-500',
      icon: 'tw-text-white'
    }
  };

  const baseButtonStyles = [
    'tw-bg-gray-50',
    'tw-text-default', 
    'tw-rounded-md',
    'tw-w-full',
    'tw-flex',
    'tw-items-center',
    'tw-justify-between',
    'tw-border',
    'tw-relative',
    'tw-min-h-0',
    'hover:tw-bg-gray-300',
    'hover:tw-text-gray-700',
    'active:tw-bg-gray-400',
    getResponsivePadding()
  ];

  const buttonClassName = [...baseButtonStyles, ...(styles.button || [])].join(' ');

  const baseDropdownStyles = [
    'tw-absolute',
    'tw-bg-white',
    'tw-border',
    'tw-rounded-md',
    'tw-shadow-lg',
    'tw-z-50',
    'tw-w-full',
    'tw-mt-1'
  ];

  const dropdownClass = [...baseDropdownStyles, ...(styles.dropdown || [])].join(' ');

  const baseListItemStyles = [
    'tw-px-4',
    'tw-py-2',
    'hover:tw-bg-blue-100',
    'tw-flex',
    'tw-items-center',
    'tw-cursor-pointer',
    'tw-break-words'
  ];

  const listItemClassName = [...baseListItemStyles, ...(styles.listItem || [])].join(' ');

  const getDisplayText = (text) => {
    return customDisplayText ? customDisplayText(text) : text;
  };

  const theme = checkboxThemes[checkboxStyle] || checkboxThemes.blue;

  return (
    <div ref={dropdownRef} className={`tw-relative tw-w-full tw-h-full ${className}`}>
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={buttonClassName}>
        <div className="tw-w-full tw-flex tw-items-center tw-justify-between">
          <span className="tw-flex tw-items-center tw-overflow-hidden tw-flex-1">
            <span className="tw-block tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-max-w-[160px] !tw-text-gray-600 tw-text-xs tw-font-medium">
              {getDisplayText(placeholder)}
            </span>
          </span>
          <div className="tw-flex tw-items-center tw-ml-2">
            <IconMC
              name={`${iconName}-${isOpen ? 'up' : 'down'}ward-outline`}
              size={iconSize}
              className="!tw-text-gray-600"
            />
          </div>
        </div>
      </button>

      {showRedDot && selectedValues.length > 0 && (
        <div className={`tw-absolute ${redDotPosition} tw-w-2 tw-h-2 tw-bg-red-500 tw-rounded-full tw-z-10`}></div>
      )}

      {isOpen && (
        <div className={dropdownClass}>
          <ul className="tw-py-1 tw-overflow-y-auto tw-max-h-60 tw-overflow-x-hidden tw-pl-0">
            {options.length === 0 ? (
              <li className="tw-px-4 tw-py-2 !tw-text-gray-950">
                No hay opciones disponibles
              </li>
            ) : (
              options.map((option) => (
                <li
                  key={option.id}
                  onClick={() => onItemToggle(option.id)}
                  className={listItemClassName}
                >
                  {showCheckboxes && (
                    <div className="tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center tw-mr-2">
                      {selectedValues.includes(option.id) ? (
                        <div className={`tw-w-3 tw-h-3 ${theme.selected} tw-rounded tw-flex tw-items-center tw-justify-center`}>
                          <IconMC
                            name="checkmark-outline"
                            size="xs"
                            className={theme.icon}
                          />
                        </div>
                      ) : (
                        <div className="tw-w-3 tw-h-3 tw-border tw-border-gray-300 tw-rounded"></div>
                      )}
                    </div>
                  )}
                  
                  {option.icon && (
                    <div className="tw-mr-2">
                      {typeof option.icon === 'string' ? (
                        <IconMC name={option.icon} size="sm" />
                      ) : (
                        option.icon
                      )}
                    </div>
                  )}
                  
                  <span className="tw-overflow-hidden tw-block tw-text-ellipsis tw-flex-1">
                    {option.label}
                  </span>
                </li>
              ))
            )}
          </ul>
          
          {selectedValues.length > 0 && styles.showSelectionCount && (
            <div className="tw-px-4 tw-py-2 tw-text-xs tw-text-gray-500 tw-border-t">
              {selectedValues.length} {selectedValues.length === 1 ? 'filtro seleccionado' : 'filtros seleccionados'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterDropdownMC;